import Form from '@/lib/Form'
import { validate, enums } from 'protoswipe-shared'

const FormType = {
	// Login form
	login: {
		defaults: {
			identifier: '',
			password: ''
		},
		validator: validate.login,
		json: true
	},
	// Forgot password form
	forgotPassword: {
		defaults: {
			email: ''
		},
		validator: validate.forgotPassword,
		json: true
	},
	// Reset password form
	resetPassword: {
		defaults: {
			code: '',
			password: '',
			passwordConfirmation: ''
		},
		validator: validate.resetPassword,
		json: true
	},
	// Campaign form
	campaign: {
		defaults: {
			name: '',
			terms:
				'After our friendly HiQ team approve your submission, your idea and display name will be added to the voting rounds and may even appear on the digital screens on campus.',
			dialogue: () => ({
				wall_idle: [['Hi!', 'Come over here!']],
				wall_interact: [['Ready to vote on some ideas?']],
				greeting: [
					[
						'Hi! Welcome to okHiQ!',
						"You've got ideas and we want to hear them."
					]
				],
				question: [['']],
				submit: [
					["Submit your best idea. I won't judge ... everyone else will."]
				],
				submitted: [['Thanks for your submission!']],
				voted: [
					[
						'Thanks for voting!',
						'Feel free to vote some more, or submit your own ideas.'
					]
				],
				leaderboard: [['Wow, check these out!']],
				leaderboard_empty: [
					[
						"Looks like no one's on the leaderboard yet.",
						'Want to submit an idea yourself?'
					]
				],
				gallery: [['Look at all these entries!']],
				gallery_empty: [
					[
						"Looks like the gallery's empty.",
						'Want to submit an idea yourself?'
					]
				]
			}),
			type: enums.CampaignType.text,
			mode: enums.CampaignMode.standard,
			termsLink: null,
			allowSubmissions: true,
			allowVoting: true,
			showLeaderboard: true,
			showGallery: true,
			leaderboardType: enums.LeaderboardType.list,
			wallLeaderboardType: enums.LeaderboardType.list,
			active: false
		},
		validator: validate.campaign
	},
	// Submission form
	submission: {
		defaults: {
			campaignId: null,
			type: '',
			mode: '',
			name: '',
			nickname: '',
			email: '',
			studentNumber: '',
			title: '',
			image: null,
			drawing: '',
			terms: false
		},
		captcha: enums.RecaptchaAction.submit,
		validator: validate.submission
	},
	// Submission form for admin use; stripped back and no captcha required
	adminSubmission: {
		defaults: {
			campaignId: null,
			type: '',
			nickname: '',
			studentNumber: '',
			title: '',
			image: null,
			drawing: ''
		},
		validator: validate.adminSubmission
	},
	// Admin account create/edit form
	adminAccount: {
		defaults: {
			id: null,
			username: '',
			role: null,
			email: '',
			password: ''
		},
		validator: validate.adminAccount,
		json: true
	},
	// Progress campaign round form
	progressCampaign: {
		defaults: {
			disableSubmissions: true
		},
		validator: validate.progressCampaign
	},
	// Global settings form
	globals: {
		defaults: {
			roundSizeWeb: 10,
			roundSizeWall: 5,
			leaderboardRefreshInterval: 30,
			galleryRefreshInterval: 300,
			dialogue: {
				poked: [['Hey, ouch!'], ["C'mon, stop it ..."]]
			}
		},
		validator: validate.globals
	}
}

function createForm(options) {
	// Return mixin
	return {
		data() {
			const form = new Form(options)
			return { form }
		},
		props: {
			loading: {
				type: Boolean,
				default: false
			}
		},
		computed: {
			fields() {
				return this.form.fields
			},
			values() {
				return this.form.toObject()
			},
			errors() {
				return this.form.errors || {}
			},
			submitButton() {
				return {
					invalid: !this.form.valid,
					loading: this.loading
				}
			}
		},
		methods: {
			async submit() {
				if (this.form.valid) {
					const serializeOpts = {}

					// Check if captcha is required
					if (options.captcha != null) {
						try {
							serializeOpts.token = await this.$recaptcha(options.captcha)
						} catch (error) {
							this.$emit(
								'error',
								new Error(
									"We couldn't verify that you're a human. Please try again."
								)
							)
							return false
						}
					}

					// Serialize and emit form data
					const data = this.form.serialize(serializeOpts)
					this.$emit('submit', data)
				}
			},
			cancel() {
				this.$emit('cancel')
			},
			populate(data) {
				this.form.populate(data)
			},
			reset() {
				this.form.reset()
			}
		},
		watch: {
			values(next) {
				this.$emit('change', next)
			}
		}
	}
}

export default Object.entries(FormType).reduce((acc, [type, form]) => {
	return { ...acc, [type]: createForm(form) }
}, {})
